import type { ForwardedRef } from 'react';
import { Fragment, forwardRef } from 'react';

import { c } from 'ttag';

import { useOrganization } from '@proton/account/organization/hooks';
import { useUser } from '@proton/account/user/hooks';
import { useUserSettings } from '@proton/account/userSettings/hooks';
import { useApi } from '@proton/app-context/useApi';
import { useConfig } from '@proton/app-context/useConfig';
import { IcAppSwitch } from '@proton/icons/icons/IcAppSwitch';
import { TelemetryAppSwitcher, TelemetryMeasurementGroups } from '@proton/shared/lib/api/telemetry';
import { getAvailableApps } from '@proton/shared/lib/apps/apps';
import type { APP_NAMES } from '@proton/shared/lib/constants';
import { APPS, BRAND_NAME } from '@proton/shared/lib/constants';
import { isElectronMail } from '@proton/shared/lib/helpers/desktop';
import { sendTelemetryReport } from '@proton/shared/lib/helpers/metrics';
import { isPassUser } from '@proton/shared/lib/helpers/usedClientsFlags';
import type { OrganizationExtended, UserModel } from '@proton/shared/lib/interfaces';
import { useFlag } from '@proton/unleash/useFlag';

import SimpleDropdown from '../../components/dropdown/SimpleDropdown';
import type { AppLinkProps } from '../../components/link/AppLink';
import { InboxDesktopAppSwitcher } from '../desktop/InboxDesktopAppSwitcher';
import ProductIcon from './ProductIcon';
import ProductLink from './ProductLink';

interface AppsDropdownProps {
    onDropdownClick?: () => void;
    app?: APP_NAMES;
    user?: UserModel;
    organization?: OrganizationExtended;
    title?: string;
    reloadDocument?: AppLinkProps['reloadDocument'];
    onAppClick?: (appToLinkTo: APP_NAMES) => void;
}

const AppsDropdown = forwardRef<HTMLButtonElement, AppsDropdownProps>(
    (
        { onDropdownClick, app, user, organization, title, reloadDocument, onAppClick, ...rest }: AppsDropdownProps,
        ref: ForwardedRef<HTMLButtonElement>
    ) => {
        const { APP_NAME } = useConfig();
        const isAuthenticatorAvailable = useFlag('AuthenticatorSettingsEnabled');
        const isSpacesAvailable = useFlag('SpacesAvailable');

        const availableApps = getAvailableApps({
            context: 'dropdown',
            user,
            organization,
            isAuthenticatorAvailable,
            isSpacesAvailable,
        });

        if (availableApps.length <= 1) {
            return null;
        }

        return (
            <SimpleDropdown
                type="button"
                hasCaret={false}
                content={<IcAppSwitch size={6} className="apps-dropdown-button-icon shrink-0 no-print" />}
                className="apps-dropdown-button shrink-0"
                dropdownClassName="apps-dropdown rounded-lg"
                originalPlacement="bottom-start"
                title={title ? title : c('Apps dropdown').t`${BRAND_NAME} applications`}
                onClick={onDropdownClick}
                disableDefaultArrowNavigation
                {...rest}
                ref={ref}
                as="button"
            >
                <ul
                    className="unstyled my-0 p-4"
                    style={{ '--apps-dropdown-repeat': isElectronMail || availableApps.length <= 2 ? '2' : '3' }}
                >
                    {availableApps.map((appToLinkTo) => {
                        const current = app && appToLinkTo === app;

                        return (
                            <Fragment key={appToLinkTo}>
                                <li className="dropdown-item apps-dropdown-item" data-testid="apps-dropdown-item">
                                    <ProductLink
                                        ownerApp={APP_NAME}
                                        app={app}
                                        user={user}
                                        appToLinkTo={appToLinkTo}
                                        className="text-center text-no-decoration outline-none--at-all apps-dropdown-link flex flex-column items-center"
                                        current={current}
                                        reloadDocument={reloadDocument}
                                        // The same app opens in the same window, other apps in new windows
                                        target={APP_NAME === appToLinkTo ? '_self' : '_blank'}
                                        onAppClick={() => onAppClick?.(appToLinkTo)}
                                    >
                                        <ProductIcon appToLinkTo={appToLinkTo} current={current} />
                                    </ProductLink>
                                </li>
                            </Fragment>
                        );
                    })}
                </ul>
            </SimpleDropdown>
        );
    }
);

AppsDropdown.displayName = 'AppsDropdown';

export const UnAuthenticatedAppsDropdown = AppsDropdown;

const AuthenticatedAppsDropdown = forwardRef<HTMLButtonElement, AppsDropdownProps>(
    (props: AppsDropdownProps, ref: ForwardedRef<HTMLButtonElement>) => {
        const [user] = useUser();
        const [organization] = useOrganization();
        const { APP_NAME } = useConfig();
        const api = useApi();
        const [userSettings] = useUserSettings();

        const handleAppClick = (toApp: APP_NAMES) => {
            try {
                const { UsedClientFlags } = userSettings;
                if (!UsedClientFlags) {
                    return;
                }

                if (toApp === APPS.PROTONPASS) {
                    const alreadyUsedPass = isPassUser(BigInt(UsedClientFlags));

                    void sendTelemetryReport({
                        api,
                        measurementGroup: TelemetryMeasurementGroups.appSwitcher,
                        event: TelemetryAppSwitcher.dropdown_app_click,
                        dimensions: {
                            fromApp: APP_NAME,
                            toApp: toApp,
                            alreadyUsedApp: alreadyUsedPass.toString(),
                        },
                        delay: false,
                    });
                }
            } catch {}
        };

        // The desktop Mail app (Mail, Calendar, Account) has its own app switcher
        if (isElectronMail) {
            return <InboxDesktopAppSwitcher appToLinkTo={props.app} />;
        }

        return (
            <AppsDropdown ref={ref} {...props} user={user} organization={organization} onAppClick={handleAppClick} />
        );
    }
);

AuthenticatedAppsDropdown.displayName = 'AuthenticatedAppsDropdown';

export default AuthenticatedAppsDropdown;
